/**
 * GDSCartAndShareManager.js
 * Manages "Add to Cart" and "Share" functionality.
 */
export class GDSCartAndShareManager {
    constructor(builder) {
        this.builder = builder;
        this.state = builder.stateManager;
        this.api = builder.apiService;
        this.uiFeedback = builder.uiFeedback;

        // Bind methods for event listeners
        this.handleAddToCartClick = this.handleAddToCartClick.bind(this);
        this.handleShareClick = this.handleShareClick.bind(this);
    }

    /**
     * Sets up event listeners for cart and share buttons
     * This method is called by GDSCardManager after rendering cards
     */
    setupListeners() {
        // Add to cart buttons
        const addToCartBtn = document.getElementById('add-to-cart-btn');
        const addToCartBtnDesktop = document.getElementById('add-to-cart-btn-desktop');

        if (addToCartBtn) {
            addToCartBtn.removeEventListener('click', this.handleAddToCartClick);
            addToCartBtn.addEventListener('click', this.handleAddToCartClick);
        }

        if (addToCartBtnDesktop) {
            addToCartBtnDesktop.removeEventListener('click', this.handleAddToCartClick);
            addToCartBtnDesktop.addEventListener('click', this.handleAddToCartClick);
        }

        // Share buttons
        const shareBtn = document.getElementById('share-btn');
        const shareBtnDesktop = document.getElementById('share-btn-desktop');

        if (shareBtn) {
            shareBtn.removeEventListener('click', this.handleShareClick);
            shareBtn.addEventListener('click', this.handleShareClick);
        }

        if (shareBtnDesktop) {
            shareBtnDesktop.removeEventListener('click', this.handleShareClick);
            shareBtnDesktop.addEventListener('click', this.handleShareClick);
        }
    }

    /**
     * Removes event listeners
     */
    removeListeners() {
        const addToCartBtn = document.getElementById('add-to-cart-btn');
        const addToCartBtnDesktop = document.getElementById('add-to-cart-btn-desktop');
        const shareBtn = document.getElementById('share-btn');
        const shareBtnDesktop = document.getElementById('share-btn-desktop');

        if (addToCartBtn) {
            addToCartBtn.removeEventListener('click', this.handleAddToCartClick);
        }

        if (addToCartBtnDesktop) {
            addToCartBtnDesktop.removeEventListener('click', this.handleAddToCartClick);
        }

        if (shareBtn) {
            shareBtn.removeEventListener('click', this.handleShareClick);
        }

        if (shareBtnDesktop) {
            shareBtnDesktop.removeEventListener('click', this.handleShareClick);
        }
    }

    /**
     * Handles share button clicks
     */
    async handleShareClick(e) {
        e.preventDefault();
        await this.copyShareUrl();
    }

    async handleAddToCartClick(e) {
        e.preventDefault();

        const items = this.getSelectedProductsForCart();
        if (items.length === 0) {
            this.uiFeedback.showError('Please select products to add to cart.');
            return;
        }
        try {
            const cartState = await this.api.addToCart(items);
            const minicart = document.querySelector('mini-cart');
            if (minicart && typeof minicart.refresh === 'function') {
                minicart.refresh(cartState);
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            this.uiFeedback.showError(error.message || 'There was a problem adding products to your cart.');
        }
    }

    async copyShareUrl() {
        try {
            await navigator.clipboard.writeText(window.location.href);
            this.uiFeedback.showConfirmation('URL copied to clipboard!');
        } catch (err) {
            console.error('Failed to copy URL: ', err);
            this.uiFeedback.showError('Could not copy URL.');
        }
    }

    getSelectedProductsForCart() {
        const selectedProducts = [];
        const selectedCards = this.builder.contentContainer.querySelectorAll('.product-card[data-selected="true"]');
        selectedCards.forEach(card => {
            const variantId = card.dataset.variantId;
            const isAvailable = card.dataset.variantAvailable === 'true';
            if (!variantId || !isAvailable) return;

            const item = {
                id: Number(variantId),
                quantity: 1,
                properties: { '_GDS_Builder_URL': window.location.href }
            };

            const builderContext = this.state.context.context || {};
            for (const [key, value] of Object.entries(builderContext)) {
                if (value) {
                    const propertyKey = '_GDS_Builder_' + key.replace(/ /g, '_');
                    item.properties[propertyKey] = value;
                }
            }
            selectedProducts.push(item);
        });
        return selectedProducts;
    }

    buildSelectionsForURL() {
        const selections = { main_cards: {}, additional_cards: {} };
        if (!this.builder.contentContainer) {
             console.error("[buildSelectionsForURL] Content container is null.");
             return selections;
        }
        this.builder.contentContainer.querySelectorAll('.product-card[data-card-index="0"]').forEach(card => {
            const cardTitle = card.dataset.cardTitle;
            const cardSelections = this._getCardSelectionsForURL(card);
            const containerKey = card.hasAttribute('data-additional-card') ? 'additional_cards' : 'main_cards';
            selections[containerKey][cardTitle] = cardSelections;
        });
        return selections;
    }

    _getCardSelectionsForURL(card) {
        const selections = JSON.parse(card.dataset.selections || '{}');
        const cleanSelections = {};
        Object.keys(selections).forEach(key => {
            if (!key.startsWith('_')) {
                cleanSelections[key] = selections[key];
            }
        });
        cleanSelections.product_handle = card.dataset.productHandle;
        cleanSelections.selected = card.dataset.selected === 'true';
        cleanSelections.secondary_cards = this._getSecondaryCardsInfo(card);
        return cleanSelections;
    }

    _getSecondaryCardsInfo(mainCard) {
        if (!mainCard) return null;
        const cardTitle = mainCard.getAttribute('data-card-title');
        const container = mainCard.closest('#main-cards-container, #additional-cards-container');
        if (!container || !cardTitle) return null;

        const selector = `.product-card[data-card-title="${cardTitle}"][data-secondary-index]:not([data-secondary-index="0"])`;
        const secondaryCards = Array.from(container.querySelectorAll(selector));
        if (secondaryCards.length === 0) return null;

        return secondaryCards.map(card => ({
            product_handle: card.getAttribute('data-product-handle'),
            selected: card.hasAttribute('data-selected'),
            index: card.getAttribute('data-secondary-index')
        }));
    }

    async applySelectionsFromURL(selections) {
        if (!selections || this.state.currentStep !== 3) return;
        this.uiFeedback.showLoading();
        try {
            const { main_cards, additional_cards } = selections;
            if (main_cards) {
                const container = this.builder.contentContainer.querySelector('#main-cards-container');
                await this._applyCardSelections(main_cards, container, true);
            }
            if (additional_cards) {
                const container = this.builder.contentContainer.querySelector('#additional-cards-container');
                await this._applyCardSelections(additional_cards, container, false);
            }
            // Update subtotal after applying selections
            if (this.builder.cardManager && this.builder.cardManager.ui) {
                this.builder.cardManager.ui.updateSubtotal();
            }
        } catch (error) {
            console.error("Error applying selections from URL:", error);
            this.uiFeedback.showError("Could not apply the shared configuration.");
        } finally {
            this.uiFeedback.hideLoading();
        }
    }

    async _applyCardSelections(selectionsMap, container, isMainGroup) {
        if (!container) return;
        const cardsToKeep = new Set(Object.keys(selectionsMap));

        // Remove cards not in selections
        container.querySelectorAll('.product-card[data-card-index="0"]').forEach(card => {
            if (!cardsToKeep.has(card.dataset.cardTitle)) {
                // Use the card UI manager to remove the card group
                if (this.builder.cardManager && this.builder.cardManager.ui) {
                    this.builder.cardManager.ui.removeCardGroup(card);
                }
            }
        });

        // Update or recreate cards in selections
        for (const [cardTitle, cardSelections] of Object.entries(selectionsMap)) {
            let mainCard = container.querySelector(`.product-card[data-card-title="${cardTitle}"]`);

            if (mainCard) {
                const { product_handle, selected, secondary_cards, ...options } = cardSelections;

                // Use the data service to update the card
                if (this.builder.cardManager && this.builder.cardManager.dataService) {
                    await this.builder.cardManager.dataService.fetchAndUpdateCardData(mainCard, product_handle, options);

                    // Set selection state
                    if (this.builder.cardManager.ui) {
                        this.builder.cardManager.ui.setCardSelectedState(mainCard, selected);
                    }

                    // Handle secondary cards
                    if (secondary_cards?.length > 0) {
                        await this.builder.cardManager.dataService.handleAdditionalProducts(
                            mainCard,
                            secondary_cards.map(sc => sc.product_handle),
                            options
                        );
                    } else {
                        this.builder.cardManager.ui.removeSecondaryCards(mainCard);
                    }
                }
            }
        }
    }
}
